import React, { useState, useEffect } from "react";
import { OrderSummaryProps } from "../../utils/types";
import { LABELS } from "./OrderSummaryConstants";
import PaymentMethods from "./OrderSummaryPaymentMethods";
import OrderHeader from "./OrderSummaryOrderHeader";
import OrderStatusSelector from "./OrderSummaryOrderStatus";
import OrderSummaryActions from "./OrderSummaryActions";
import { colorOptions, fontOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { notifySuccess } from "src/assets/toastService";

const SummaryItem: React.FC<{ label: string; value: string | number | undefined }> = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 px-3 border-b border-gray-100 last:border-b-0">
    <span className="text-gray-600 text-sm font-medium">{label}</span>
    <span className="text-gray-900 font-semibold text-sm">{value ?? "0.00"}</span>
  </div>
);

const OrderSummary: React.FC<OrderSummaryProps> = ({ order, status, handleStatusChange, paymentMethod, handlePaymentMethodChange, roundValue, cancelOrder, isCancelDisabled, handlePrint }) => {
  const { user } = useUser();
  const [refundProcessed, setRefundProcessed] = useState(false);
  const [updatedReceivedAmount, setUpdatedReceivedAmount] = useState(0);

  const themeKey = user?.background_colour || "DodgerBlue";
  const bgClass = colorOptions[themeKey] || "bg-blue-500";
  const fontClass = fontOptions[user?.font_family || "Sans-serif"];

  const remainingAmount = order?.RemainingAmount ? Number(order.RemainingAmount) : 0;
  const originalReceivedAmount = order?.payments?.[0]?.ReceivedAmount ? Number(order.payments[0].ReceivedAmount) : 0;
  const isFullyPaid = remainingAmount <= 0;
  const isCancelled = order?.OrderStatus === "Cancelled";

  // Calculate actual received amount based on refund status
  const actualReceivedAmount = refundProcessed ? updatedReceivedAmount : originalReceivedAmount;

  // Show refund option if there was payment received and order is not already cancelled
  const showRefundOption = originalReceivedAmount > 0 && !isCancelled;

  // Reset refund status when order changes
  useEffect(() => {
    if (order?.OrderStatus !== "Cancelled") {
      setRefundProcessed(false);
      setUpdatedReceivedAmount(originalReceivedAmount);
    }
  }, [order?.OrderStatus, originalReceivedAmount]);

  const summaryItems = [
    { label: LABELS.ORDER_DATE, value: order?.Orderdate },
    { label: LABELS.ORDER_TIME, value: order?.Ordertime },
    { label: LABELS.PAYMENT_MODE, value: order?.payments?.[0]?.Paymentmode },
    {
      label: LABELS.SUBTOTAL,
      value: `${roundValue(order?.totalAmount ? Number(order.totalAmount) : undefined)} QAR`,
    },
    {
      label: LABELS.RECEIVED_PRICE,
      value: `${refundProcessed ? "0.00" : roundValue(actualReceivedAmount)} QAR${refundProcessed ? " (Refunded)" : ""}`,
    },
    {
      label: LABELS.REMAINING_PRICE,
      value: `${roundValue(remainingAmount)} QAR`,
    },
  ];

  // Handle delivered button click
  const handleDeliveredClick = () => {
    const syntheticEvent = {
      target: {
        value: "Delivered",
      },
    } as React.ChangeEvent<{ value: string }>;
    handleStatusChange(syntheticEvent);
  };

  // Updated cancel order to handle refund parameter
  const handleCancelWithRefund = (processRefund: boolean) => {
    if (processRefund && originalReceivedAmount > 0) {
      // Process refund logic
      console.log("Processing refund for order:", order?.Orderid, "Amount:", originalReceivedAmount);

      // Update local state to reflect refund
      setRefundProcessed(true);
      setUpdatedReceivedAmount(0);
    } else {
      // Regular cancellation without refund
      console.log("Cancelling order without refund");
      notifySuccess("Order cancelled successfully");
    }

    cancelOrder();
  };

  return (
    <div className={`${fontClass} h-full flex flex-col`}>
      {/* Compact Header */}
      <div className={`${bgClass} text-white px-4 py-2 rounded-t flex-shrink-0`}>
        <h2 className="text-lg font-bold">{LABELS.ORDER_SUMMARY}</h2>
      </div>

      {/* Compact Content */}
      <div className="p-3 space-y-3 flex-1 overflow-y-auto">
        {/* Order Header */}
        <OrderHeader orderId={order?.Orderid} customerName={order?.CustomerName} />

        {/* Summary Items */}
        <div className="bg-gray-50 rounded border border-gray-200 overflow-hidden">
          {summaryItems.map(({ label, value }, i) => (
            <SummaryItem key={i} label={label} value={value} />
          ))}
        </div>

        {/* Order Status */}
        <OrderStatusSelector
          status={status}
          orderStatus={order?.OrderStatus}
          remainingAmount={remainingAmount}
          isFullyPaid={isFullyPaid}
          onChange={handleStatusChange}
          onDeliveredClick={handleDeliveredClick}
        />

        {/* Payment Methods - Only show if not fully paid AND status is pending */}
        <PaymentMethods selected={paymentMethod} onChange={handlePaymentMethodChange} remainingAmount={remainingAmount} status={status} isFullyPaid={isFullyPaid} orderStatus={order?.OrderStatus} />

        {/* Actions */}
        <div className="pt-2">
          <OrderSummaryActions
            handlePrint={handlePrint}
            cancelOrder={handleCancelWithRefund}
            isCancelDisabled={isCancelDisabled}
            orderStatus={order?.OrderStatus}
            showRefundOption={showRefundOption}
            receivedAmount={originalReceivedAmount}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
